import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CreateEventRegistration,
  EventRegistrationBillingAddress,
  EventRegistrationSelection,
} from '../generated/api/dsv-public/model';
import { useEventRegistrationForm } from '../context/EventRegistrationForm';
import { InitialQuestionnaireForm } from '../forms/InitialQuestionnaireForm';
import { LoadingForm } from '../forms/LoadingForm';
import { ErrorForm } from '../forms/ErrorForm';
import { InactiveFrom } from '../forms/InactiveFrom';
import { SingleRegistrationForm } from '../forms/SingleRegistrationForm';
import { RegistrantInfoForm } from '../forms/RegistrantInfoForm';
import { MultiRegistrationForm } from '../forms/MultiRegistrationForm';
import { mapEntriesToRegistrant } from '../util/mapper';
import { FormNotFoundForm } from '../forms/FormNotFoundFrom';
import { EventNotFoundForm } from '../forms/EventNotFoundFrom';
import { SubmissionSuccessfulForm } from '../forms/SubmissionSuccessfulForm';
import { useFormCheckpoint } from '../context/FormCheckpoint';
import { RegistrationFormHeader } from './RegistrationFormHeader';
import { RegistrationFormStatus } from './RegistrationFormStatus';
import { SingleFormProvider } from '../context/SingleForm';
import { BillingInfo, RegistrantInfo } from '../forms/form.types';
import { BillingInfoForm } from '../forms/BillingInfoForm';
import { tracker } from '../openreplay';

type FormControllerProps = {};

export enum FormType {
  LOADING = 'LOADING',
  INITIAL_QUESTIONNAIRE = 'INITIAL_QUESTIONNAIRE',
  SINGLE_FORM = 'SINGLE_FORM',
  REGISTRANT_INFO_FORM = 'REGISTRANT_INFO_FORM',
  MULTI_FORM = 'MULTI_FORM',
  BILLING_INFO_FORM = 'BILLING_INFO_FORM',
  ERROR_FORM = 'ERROR_FORM',
  INACTIVE_FORM = 'INACTIVE_FORM',
  FORM_NOT_FOUND = 'FORM_NOT_FOUND',
  EVENT_NOT_FOUND = 'EVENT_NOT_FOUND',
  SUBMISSION_FAILED = 'SUBMISSION_FAILED',
  SUBMISSION_SUCCESSFUL = 'SUBMISSION_SUCCESSFUL',
}

export const FormController: React.FC<FormControllerProps> = () => {
  const {
    isLoading,
    isError,
    isLoadingSubmission,
    isSubmissionError,
    isInactive,
    isExistingForm,
    isExistingEvent,
    registrationForm,
    event,
    registrationFormIsActive,
    apiResponseProblem,
    errorResponseCode,
    eventRegistrationResponse,
    submitForm,
    resetFormSubmission,
  } = useEventRegistrationForm();
  const { savedActiveForm, savedRegistrationEntries, setCheckpoint } = useFormCheckpoint();
  const [activeForm, setActiveForm] = useState<FormType>(FormType.LOADING);
  const [registrant, setRegistrant] = useState<RegistrantInfo>();
  const [registrationSelection, setRegistrationSelection] = useState<EventRegistrationSelection>();
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

  const showInitialQuestionnaireForm = useMemo(() => {
    return !isLoading && !!registrationFormIsActive && !isError && !isSubmitted && !savedActiveForm;
  }, [isLoading, registrationFormIsActive, isError, isSubmitted, savedActiveForm]);

  const showBillingAddress = registrationForm?.billingAddressRequired;

  const handleSubmitInitialQuestionnaire = useCallback(
    (registrationSelection: EventRegistrationSelection) => {
      setActiveForm(FormType.REGISTRANT_INFO_FORM);
      setRegistrationSelection(registrationSelection);
      window.location.hash = '#registrant-info-form';
    },
    [setActiveForm]
  );

  const handleSubmitRegistrantInfo = useCallback(
    (registrant: RegistrantInfo) => {
      setRegistrant(registrant);

      if (registrant.email) {
        tracker.setUserID(registrant.email);
      }

      if (registrationSelection !== EventRegistrationSelection.MYSELF) {
        setActiveForm(FormType.MULTI_FORM);
        window.location.hash = '#multi-form';
      } else {
        setActiveForm(FormType.SINGLE_FORM);
        window.location.hash = '#single-form';
      }
    },
    [setRegistrant, setActiveForm, registrationSelection]
  );

  const handleSubmitForm = useCallback(
    (registrationValues: CreateEventRegistration) => {
      if (registrationSelection === undefined) {
        console.error('Cant submit empty Form');
        return;
      }
      const registrantSubmission = registrant === undefined ? mapEntriesToRegistrant(registrationValues) : registrant;
      setCheckpoint(activeForm, registrantSubmission, registrationSelection, registrationValues);
      if (showBillingAddress) {
        setActiveForm(FormType.BILLING_INFO_FORM);
        window.location.hash = '#billing-info-form';
      } else {
        submitForm(registrantSubmission, registrationSelection, registrationValues);
        setIsSubmitted(true);
      }
    },
    [registrant, registrationSelection, submitForm, setIsSubmitted, activeForm, setCheckpoint, showBillingAddress]
  );

  const handleSubmitMultiForm = useCallback(
    (registrationValues: CreateEventRegistration[]) => {
      if (registrationSelection === undefined) {
        console.error('Cant submit empty Form');
        return;
      }
      if (registrant !== undefined) {
        setCheckpoint(activeForm, registrant, registrationSelection, registrationValues);
        if (showBillingAddress) {
          setActiveForm(FormType.BILLING_INFO_FORM);
          window.location.hash = '#billing-info-form';
        } else {
          submitForm(registrant, registrationSelection, registrationValues);
          setIsSubmitted(true);
        }
      }
    },
    [submitForm, registrant, registrationSelection, setIsSubmitted, activeForm, setCheckpoint, showBillingAddress]
  );

  const handleSubmitBillingInfo = useCallback(
    (billingInfo: BillingInfo) => {
      if (registrant !== undefined && registrationSelection !== undefined && savedRegistrationEntries !== undefined) {
        setCheckpoint(activeForm, registrant, registrationSelection, savedRegistrationEntries, billingInfo);
        const billingAddress = {
          firstName: billingInfo.firstName,
          lastName: billingInfo.lastName,
          addressLine: billingInfo.addressLine,
          zipCode: billingInfo.zipCode,
          city: billingInfo.city,
          country: billingInfo.country,
          email: billingInfo.email,
        } as EventRegistrationBillingAddress;

        submitForm(
          registrant,
          registrationSelection,
          savedRegistrationEntries,
          billingAddress,
          billingInfo.billingMethod
        );
        setIsSubmitted(true);
      }
    },
    [activeForm, registrant, registrationSelection, savedRegistrationEntries, setCheckpoint, submitForm]
  );

  const handleGoBackToInitialQuestionnaire = useCallback(() => {
    if (window.location.hash.length) {
      window.location.hash = '';
    }
    setActiveForm(FormType.INITIAL_QUESTIONNAIRE);
  }, [setActiveForm]);

  const handleGoBackToRegistrantInfo = useCallback(() => {
    if (window.location.hash !== '#registrant-info-form') {
      window.location.hash = '#registrant-info-form';
    }
    setActiveForm(FormType.REGISTRANT_INFO_FORM);
  }, [setActiveForm]);

  const handleGoBackToForm = useCallback(() => {
    if (savedActiveForm === FormType.BILLING_INFO_FORM && window.location.hash !== '#billing-info-form') {
      window.location.hash = '#billing-info-form';
      setActiveForm(FormType.BILLING_INFO_FORM);
    } else if (registrationSelection === EventRegistrationSelection.MYSELF && window.location.hash !== '#single-form') {
      window.location.hash = '#single-form';
      setActiveForm(FormType.SINGLE_FORM);
    } else if (window.location.hash !== '#multi-form') {
      window.location.hash = '#multi-form';
      setActiveForm(FormType.MULTI_FORM);
    }
    setIsSubmitted(false);
    resetFormSubmission();
  }, [savedActiveForm, setActiveForm, resetFormSubmission, registrationSelection]);

  const handleGoBackToBillingInfo = useCallback(() => {
    if (window.location.hash !== '#billing-info-form') {
      window.location.hash = '#billing-info-form';
    }
    setActiveForm(FormType.BILLING_INFO_FORM);
  }, []);

  useEffect(() => {
    // Handles navigation via browser back/forward buttons
    const handleHashChange = (event: HashChangeEvent) => {
      const previousHash = event.oldURL.split('#')[1];
      const currentHash = window.location.hash.substring(1);

      if (!currentHash && previousHash === 'registrant-info-form') {
        // Registrant Info Form -> Initial Questionnaire Form (Backward)
        handleGoBackToInitialQuestionnaire();
      } else if (
        (previousHash === 'single-form' || previousHash === 'multi-form') &&
        currentHash === 'registrant-info-form'
      ) {
        // Single/multi Form -> Registrant Info Form (Backward)
        handleGoBackToRegistrantInfo();
      } else if (
        previousHash === 'submission-error' &&
        (currentHash === 'single-form' || currentHash === 'multi-form')
      ) {
        // Submission Error -> Single/Multi Form (Backward)
        handleGoBackToForm();
      } else if (previousHash === 'submission-error' && currentHash === 'billing-info-form') {
        // Submission Error -> Billing Info Form (Backward)
        handleGoBackToBillingInfo();
      } else if (previousHash === 'submission-successful') {
        // Submission Successful (Backward) -> delete hash
        window.location.assign(window.location.href.replace(/#.*$/, ''));
      } else if (
        previousHash === 'registrant-info-form' &&
        (currentHash === 'single-form' || currentHash === 'multi-form')
      ) {
        // Registrant Info Form -> Single/Multi Form (Forward)
        handleSubmitRegistrantInfo(registrant ?? ({} as RegistrantInfo));
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    if (window.location.hash.length && activeForm === FormType.INITIAL_QUESTIONNAIRE) {
      // Remove hash from URL on refresh
      window.location.assign(window.location.href.replace(/#.*$/, ''));
    }

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [
    handleGoBackToInitialQuestionnaire,
    handleGoBackToRegistrantInfo,
    handleGoBackToForm,
    handleGoBackToBillingInfo,
    handleSubmitRegistrantInfo,
    registrant,
    activeForm,
  ]);

  useEffect(() => {
    if ((isLoading && !isError) || (isSubmitted && isLoadingSubmission)) {
      setActiveForm(FormType.LOADING);
    } else if (showInitialQuestionnaireForm) {
      setActiveForm(FormType.INITIAL_QUESTIONNAIRE);
    } else if (isInactive) {
      setActiveForm(FormType.INACTIVE_FORM);
    } else if (!isExistingEvent) {
      setActiveForm(FormType.EVENT_NOT_FOUND);
    } else if (!isExistingForm) {
      setActiveForm(FormType.FORM_NOT_FOUND);
    } else if (isError) {
      setActiveForm(FormType.ERROR_FORM);
    } else if (isSubmitted && !isLoadingSubmission && isSubmissionError) {
      setActiveForm(FormType.SUBMISSION_FAILED);
    } else if (isSubmitted && !isLoadingSubmission && !isSubmissionError) {
      setActiveForm(FormType.SUBMISSION_SUCCESSFUL);
    }
  }, [
    isLoading,
    showInitialQuestionnaireForm,
    isError,
    isInactive,
    isExistingForm,
    isExistingEvent,
    isSubmitted,
    isLoadingSubmission,
    isSubmissionError,
  ]);

  const renderForm = useMemo(() => {
    switch (activeForm) {
      case FormType.LOADING:
        return <LoadingForm />;
      case FormType.INITIAL_QUESTIONNAIRE:
        return (
          <InitialQuestionnaireForm
            event={event}
            registrationForm={registrationForm}
            handleSubmitInitialQuestionnaire={handleSubmitInitialQuestionnaire}
          />
        );
      case FormType.SINGLE_FORM:
        return (
          <SingleFormProvider>
            <SingleRegistrationForm
              registrant={registrant}
              registrationForm={registrationForm}
              handleSubmitForm={handleSubmitForm}
              handleGoBack={handleGoBackToRegistrantInfo}
            />
          </SingleFormProvider>
        );
      case FormType.REGISTRANT_INFO_FORM:
        return (
          <RegistrantInfoForm
            prefilledRegistrant={registrant}
            registrationForm={registrationForm}
            registrationSelection={registrationSelection}
            handleSubmitRegistrantInfo={handleSubmitRegistrantInfo}
            handleGoBack={handleGoBackToInitialQuestionnaire}
          />
        );
      case FormType.MULTI_FORM:
        return (
          <MultiRegistrationForm
            registrationForm={registrationForm}
            registrant={registrant}
            handleSubmitForm={handleSubmitMultiForm}
            isSameFamily={!registrationSelection?.includes(EventRegistrationSelection.ME_AND_OTHERS)}
            copyRegistrantFields={!registrationSelection?.includes(EventRegistrationSelection.FAMILY_MEMBERS)}
            isFamilyMembers={!!registrationSelection?.includes(EventRegistrationSelection.FAMILY_MEMBERS)}
            handleGoBack={handleGoBackToRegistrantInfo}
          />
        );
      case FormType.BILLING_INFO_FORM:
        return (
          <BillingInfoForm
            registrant={registrant}
            handleSubmitBillingInfo={handleSubmitBillingInfo}
            handleGoBack={handleGoBackToForm}
          />
        );
      case FormType.ERROR_FORM:
        return <ErrorForm problem={apiResponseProblem} errorResponseCode={errorResponseCode} />;
      case FormType.INACTIVE_FORM:
        return <InactiveFrom />;
      case FormType.FORM_NOT_FOUND:
        return <FormNotFoundForm />;
      case FormType.EVENT_NOT_FOUND:
        return <EventNotFoundForm />;
      case FormType.SUBMISSION_SUCCESSFUL:
        return <SubmissionSuccessfulForm eventRegistrationResponse={eventRegistrationResponse} />;
      case FormType.SUBMISSION_FAILED:
        return (
          <ErrorForm
            problem={apiResponseProblem}
            errorResponseCode={errorResponseCode}
            isSubmissionError={true}
            canGoBack={true}
            goBack={handleGoBackToForm}
          />
        );
    }
  }, [
    event,
    activeForm,
    handleSubmitInitialQuestionnaire,
    registrationForm,
    handleSubmitForm,
    handleSubmitRegistrantInfo,
    handleSubmitMultiForm,
    handleSubmitBillingInfo,
    apiResponseProblem,
    errorResponseCode,
    registrant,
    registrationSelection,
    eventRegistrationResponse,
    handleGoBackToForm,
    handleGoBackToInitialQuestionnaire,
    handleGoBackToRegistrantInfo,
  ]);

  return (
    <>
      {!isError && event && !isLoading && !isLoadingSubmission && (
        <RegistrationFormHeader event={event} activeForm={activeForm} showBillingAddress={showBillingAddress} />
      )}
      <RegistrationFormStatus registrationForm={registrationForm} />
      {renderForm}
    </>
  );
};
